import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import Navbar from "../../components/navbar";
import Footer from "../../components/footer";
import authService from "../../services/authService";
import { Oval } from 'react-loader-spinner';
import { Modal, Button } from 'react-bootstrap';

export default function Dashboard() {
    const navigate = useNavigate();
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);
    const [referralData, setReferralData] = useState(null);
    const [generating, setGenerating] = useState(false);
    const [error, setError] = useState(null);
    const [profileComplete, setProfileComplete] = useState(false);
    const [commissionStats, setCommissionStats] = useState(null);
    const [withdrawalAmount, setWithdrawalAmount] = useState('');
    const [withdrawalLoading, setWithdrawalLoading] = useState(false);
    const [withdrawalError, setWithdrawalError] = useState(null);
    const [showWithdrawalModal, setShowWithdrawalModal] = useState(false);
    const [withdrawalSuccess, setWithdrawalSuccess] = useState(false);

    useEffect(() => {
        // Check if user is logged in
        const checkAuth = async () => {
            try {
                if (!authService.isLoggedIn()) {
                    navigate('/auth-login');
                    return;
                }

                // Get current user data
                const currentUser = authService.getCurrentUser();
                setUser(currentUser);

                // Check if profile is complete
                try {
                    const profileData = await authService.verifyProfileCompletion();
                    
                    // Use the isComplete flag from the response
                    setProfileComplete(profileData.isComplete);
                    
                    // Update user with latest data from API
                    if (profileData.user) {
                        setUser(profileData.user);
                        // Update localStorage with the latest user data
                        localStorage.setItem('user', JSON.stringify(profileData.user));
                    }
                } catch (err) {
                    console.error("Error checking profile completion:", err);
                    setProfileComplete(false);
                }

                // Check if user already has a referral code
                try {
                    const referralResponse = await authService.getUserReferral();
                    if (referralResponse.has_referral) {
                        setReferralData(referralResponse.referral);
                    }
                } catch (err) {
                    console.error("Error fetching referral:", err);
                }

                // Get commission stats
                try {
                    const commissionResponse = await authService.getCommissionStats();
                    setCommissionStats(commissionResponse);
                } catch (err) {
                    console.error("Error fetching commission stats:", err);
                }

                setLoading(false);
            } catch (err) {
                console.error("Authentication error:", err);
                navigate('/auth-login');
            }
        };

        checkAuth();
    }, [navigate]);

    const handleGenerateReferral = async () => {
        if (!profileComplete) {
            setError("Please complete your profile before generating a referral code.");
            return;
        }

        setGenerating(true);
        setError(null);

        try {
            const response = await authService.generateReferral();
            setReferralData(response.referral);
        } catch (err) {
            setError(err.message || "Failed to generate referral code. Please try again.");
            console.error(err);
        } finally {
            setGenerating(false);
        }
    };

    const getReferralLink = () => {
        if (!referralData || !referralData.code) return "";
        return `booking.gondola-ancol.com/reservation/${referralData.code}`;
    };

    const copyToClipboard = (text) => {
        navigator.clipboard.writeText(text).then(() => {
            alert("Referral link copied to clipboard!");
        }).catch(err => {
            console.error('Failed to copy: ', err);
        });
    };

    const handleWithdrawalRequest = async () => {
        setWithdrawalLoading(true);
        setWithdrawalError(null);
        
        try {
            // Validate withdrawal amount
            if (!withdrawalAmount || isNaN(withdrawalAmount) || parseFloat(withdrawalAmount) <= 0) {
                throw new Error('Please enter a valid amount');
            }
            
            // Ensure withdrawal amount doesn't exceed available balance
            if (parseFloat(withdrawalAmount) > commissionStats.available_balance) {
                throw new Error('Withdrawal amount exceeds your available balance');
            }
            
            await authService.requestWithdrawal({ amount: parseFloat(withdrawalAmount) });
            setWithdrawalSuccess(true);
            
            // Refresh commission stats
            const commissionResponse = await authService.getCommissionStats();
            setCommissionStats(commissionResponse);
            
            // Reset form
            setWithdrawalAmount('');
        } catch (err) {
            setWithdrawalError(err.message || 'Failed to request withdrawal. Please try again.');
            console.error(err);
        } finally {
            setWithdrawalLoading(false);
        }
    };
    
    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('id-ID', { style: 'currency', currency: 'IDR' }).format(amount);
    };

    if (loading) {
        return (
            <div className="min-vh-100 d-flex justify-content-center align-items-center">
                <Oval
                    visible={true}
                    height="80"
                    width="80"
                    color="#4fa94d"
                    ariaLabel="oval-loading"
                    wrapperStyle={{}}
                    wrapperClass=""
                />
                <p className="ms-3">Memuat dashboard...</p>
            </div>
        );
    }

    return (
        <>
            <Navbar navClass="defaultscroll sticky" menuClass="navigation-menu nav-left" />
            
            <section className="section mt-5 pt-4">
                <div className="container mt-100 mt-60">
                    <div className="row">
                        <div className="col-lg-4 col-md-6 col-12 d-lg-block">
                            <div className="sidebar sticky-bar p-4 rounded bg-light">
                                <div className="text-center">
                                    <h5 className="mb-0">{user?.name || "Pengguna"}</h5>
                                    <p className="text-muted mt-1 mb-0">{user?.email}</p>
                                </div>

                                <div className="mt-4">
                                    <ul className="list-unstyled sidebar-nav mb-0">
                                        <li className="navbar-item active"><Link to="/user/dashboard" className="navbar-link"><i className="mdi mdi-view-dashboard me-2"></i>Dashboard</Link></li>
                                        <li className="navbar-item"><Link to="/user/commissions" className="navbar-link"><i className="mdi mdi-currency-usd me-2"></i>Komisi</Link></li>
                                        <li className="navbar-item"><Link to="/user/profile" className="navbar-link"><i className="mdi mdi-account me-2"></i>Profil</Link></li>
                                        <li className="navbar-item">
                                            <Link to="#" className="navbar-link" onClick={() => {
                                                authService.logout();
                                                navigate('/auth-login');
                                            }}><i className="mdi mdi-logout me-2"></i>Keluar</Link>
                                        </li>
                                    </ul>
                                </div>
                            </div>
                        </div>

                        <div className="col-lg-8 col-12">
                            <div className="card border-0 rounded shadow">
                                <div className="card-body">
                                    <h5 className="mb-3">Selamat datang, {user?.name || "Pengguna"}!</h5>
                                    
                                    {commissionStats && (
                                        <div className="row mb-4">
                                            <div className="col-md-12">
                                                <div className="p-4 bg-light rounded">
                                                    <h6>Ringkasan Komisi Anda</h6>
                                                    <div className="row mt-3">
                                                        <div className="col-md-4">
                                                            <div className="card border-0 bg-white p-3">
                                                                <h6 className="text-muted">Saldo Tersedia</h6>
                                                                <h4>{formatCurrency(commissionStats.available_balance || 0)}</h4>
                                                            </div>
                                                        </div>
                                                        <div className="col-md-4">
                                                            <div className="card border-0 bg-white p-3">
                                                                <h6 className="text-muted">Total Diterima</h6>
                                                                <h4>{formatCurrency(commissionStats.total_earned || 0)}</h4>
                                                            </div>
                                                        </div>
                                                        <div className="col-md-4">
                                                            <div className="card border-0 bg-white p-3">
                                                                <h6 className="text-muted">Tertunda</h6>
                                                                <h4>{formatCurrency(commissionStats.pending_amount || 0)}</h4>
                                                            </div>
                                                        </div>
                                                    </div>
                                                    
                                                    <div className="mt-3 d-flex justify-content-between align-items-center">
                                                        {commissionStats.available_balance > 0 && (
                                                            <button 
                                                                className="btn btn-primary"
                                                                onClick={() => setShowWithdrawalModal(true)}
                                                            >
                                                                <i className="mdi mdi-cash-multiple me-2"></i>
                                                                Tarik Dana
                                                            </button>
                                                        )}
                                                        
                                                        <Link to="/user/commissions" className="btn btn-outline-primary">
                                                            <i className="mdi mdi-eye me-1"></i> Lihat Semua Komisi
                                                        </Link>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                    
                                    <div className="row">
                                        <div className="col-md-12">
                                            <div className="p-4 bg-light rounded">
                                                <h6>Status Referral Anda</h6>
                                                
                                                {!profileComplete ? (
                                                    <div className="alert alert-warning mt-3" role="alert">
                                                        <strong>Profil Anda belum lengkap!</strong><br />
                                                        Silakan <Link to="/user/profile" className="alert-link">lengkapi profil Anda</Link> untuk dapat membuat kode referral.
                                                    </div>
                                                ) : (
                                                    <div className="mt-3">
                                                        {error && (
                                                            <div className="alert alert-danger" role="alert">
                                                                {error}
                                                            </div>
                                                        )}
                                                        
                                                        {referralData ? (
                                                            <div className="mb-3">
                                                                <label className="form-label">Kode Referral Anda: <span className="fw-bold">{referralData.code}</span></label>
                                                                <div className="input-group mt-2">
                                                                    <input 
                                                                        type="text" 
                                                                        className="form-control" 
                                                                        value={getReferralLink()} 
                                                                        readOnly 
                                                                    />
                                                                    <button 
                                                                        className="btn btn-primary" 
                                                                        onClick={() => copyToClipboard(getReferralLink())}
                                                                    >
                                                                        <i className="mdi mdi-content-copy"></i>
                                                                    </button>
                                                                </div>
                                                                <small className="text-muted mt-2 d-block">Bagikan link ini dengan teman Anda dan dapatkan komisi!</small>
                                                            </div>
                                                        ) : (
                                                            <button
                                                                className="btn btn-primary mt-2"
                                                                onClick={handleGenerateReferral}
                                                                disabled={generating}
                                                            >
                                                                {generating ? (
                                                                    <>
                                                                        <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                                                        Membuat...
                                                                    </>
                                                                ) : "Buat Link Referral"}
                                                            </button>
                                                        )}
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </div>

                                    <div className="row mt-4">
                                        <div className="col-md-12">
                                            <div className="p-4 bg-light rounded">
                                                <h6>Tindakan Cepat</h6>
                                                <div className="d-flex flex-wrap gap-2 mt-3">
                                                    <Link to="/user/profile" className="btn btn-outline-primary">
                                                        <i className="mdi mdi-account-edit me-2"></i>Perbarui Profil
                                                    </Link>
                                                    <Link to="/contactus" className="btn btn-outline-primary">
                                                        <i className="mdi mdi-help-circle me-2"></i>Dapatkan Bantuan
                                                    </Link>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Withdrawal Modal */}
            <Modal show={showWithdrawalModal} onHide={() => {
                setShowWithdrawalModal(false);
                setWithdrawalError(null);
                setWithdrawalSuccess(false);
                setWithdrawalAmount('');
            }}>
                <Modal.Header closeButton>
                    <Modal.Title>Tarik Dana</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {withdrawalSuccess ? (
                        <div className="alert alert-success" role="alert">
                            Permintaan penarikan dana Anda telah berhasil dikirim!
                        </div>
                    ) : (
                        <>
                            {withdrawalError && (
                                <div className="alert alert-danger" role="alert">
                                    {withdrawalError}
                                </div>
                            )}
                            <p>Saldo Tersedia: <strong>{formatCurrency(commissionStats?.available_balance || 0)}</strong></p>
                            <div className="mb-3">
                                <label htmlFor="withdrawalAmount" className="form-label">Jumlah Penarikan</label>
                                <div className="input-group">
                                    <span className="input-group-text">Rp</span>
                                    <input
                                        type="number"
                                        className="form-control"
                                        id="withdrawalAmount"
                                        placeholder="Masukkan jumlah"
                                        value={withdrawalAmount}
                                        onChange={(e) => setWithdrawalAmount(e.target.value)}
                                    />
                                </div>
                            </div>
                            <p className="text-muted small">
                                Permintaan penarikan diproses dalam 1-3 hari kerja.
                                Dana akan ditransfer ke rekening bank terdaftar Anda.
                            </p>
                        </>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    {withdrawalSuccess ? (
                        <Button variant="primary" onClick={() => setShowWithdrawalModal(false)}>
                            Tutup
                        </Button>
                    ) : (
                        <>
                            <Button variant="secondary" onClick={() => setShowWithdrawalModal(false)}>
                                Batal
                            </Button>
                            <Button 
                                variant="primary" 
                                onClick={handleWithdrawalRequest}
                                disabled={withdrawalLoading}
                            >
                                {withdrawalLoading ? (
                                    <>
                                        <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                        Memproses...
                                    </>
                                ) : "Kirim Permintaan"}
                            </Button>
                        </>
                    )}
                </Modal.Footer>
            </Modal>
            <Footer />
        </>
    );
} 